'use client';

import { useState, useEffect } from 'react';

import { Message } from 'ai';
import { useChat } from 'ai/react';
import {
  Globe,
  Search,
  MapPin,
  Plane,
  Hotel,
  Car,
  Utensils,
  Calendar,
  TrendingUp,
  Sparkles,
  ArrowUp,
  Square,
  MessageCirclePlus,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { SessionProvider } from 'next-auth/react';
import Textarea from 'react-textarea-autosize';
import { toast } from 'sonner';

import CloudinaryImage from '@/components/CloudinaryImage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';

// Replace AIChatWrapper with our new enhanced TravelChat component
const TravelChat = ({
  id = 'next-gen-search',
  savedMessages = [],
  query,
}: {
  id: string;
  savedMessages?: Message[];
  query?: string;
}) => {
  const router = useRouter();
  const [isComposing, setIsComposing] = useState(false);
  const [enterDisabled, setEnterDisabled] = useState(false);
  const [showEmptyScreen, setShowEmptyScreen] = useState(false);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    setMessages,
    stop,
    append,
    setData,
  } = useChat({
    initialMessages: savedMessages,
    id: id,
    body: {
      id,
    },
    onFinish: () => {
      window.history.replaceState({}, '', `/next-gen-travel-search/${id}`);
    },
    onError: error => {
      toast.error(`Error in chat: ${error.message}`);
    },
    sendExtraMessageFields: false,
  });

  useEffect(() => {
    setMessages(savedMessages);
  }, [id, savedMessages, setMessages]);

  const onQuerySelect = (query: string) => {
    append({
      role: 'user',
      content: query,
    });
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setData(undefined); // reset data to clear tool call
    handleSubmit(e);
  };

  const handleNewChat = () => {
    setMessages([]);
    router.push('/next-gen-travel-search');
  };

  const handleCompositionStart = () => setIsComposing(true);

  const handleCompositionEnd = () => {
    setIsComposing(false);
    setEnterDisabled(true);
    setTimeout(() => {
      setEnterDisabled(false);
    }, 300);
  };

  useEffect(() => {
    if (query && query.trim().length > 0) {
      append({
        role: 'user',
        content: query,
      });
    }
  }, [query, append]);

  // Chat messages display component
  const renderChatMessages = () => {
    return (
      <div className="space-y-4 mb-8">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg ${
              message.role === 'user' ? 'bg-primary/10 ml-auto max-w-[80%]' : 'bg-muted max-w-[80%]'
            }`}
          >
            <div className="font-medium mb-1">
              {message.role === 'user' ? 'You' : 'AI Travel Assistant'}
            </div>
            <div className="text-sm whitespace-pre-wrap">{message.content}</div>
          </div>
        ))}
        {isLoading && (
          <div className="p-4 rounded-lg bg-muted max-w-[80%]">
            <div className="font-medium mb-1">AI Travel Assistant</div>
            <div className="flex space-x-2">
              <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce [animation-delay:0.2s]"></div>
              <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce [animation-delay:0.4s]"></div>
              <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce [animation-delay:0.6s]"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full">
      {messages.length > 0 && (
        <div className="max-h-[400px] overflow-y-auto py-4 px-1">{renderChatMessages()}</div>
      )}

      <form onSubmit={onSubmit} className="w-full">
        <div className="relative flex flex-col w-full gap-2 bg-muted rounded-xl border border-input">
          <Textarea
            name="input"
            rows={2}
            maxRows={5}
            tabIndex={0}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder="Ask about destinations, travel tips, or plan your trip..."
            spellCheck={false}
            value={input}
            className="resize-none w-full min-h-12 bg-transparent border-0 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onChange={e => {
              handleInputChange(e);
              setShowEmptyScreen(e.target.value.length === 0);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey && !isComposing && !enterDisabled) {
                if (input.trim().length === 0) {
                  e.preventDefault();
                  return;
                }
                e.preventDefault();
                const textarea = e.target as HTMLTextAreaElement;
                textarea.form?.requestSubmit();
              }
            }}
          />

          {/* Bottom menu area */}
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-medium flex items-center">
                <Sparkles className="h-3 w-3 mr-1" />
                <span>AI Travel Agent</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="p-2 rounded-full border border-input bg-background hover:bg-accent transition-colors duration-200 shrink-0 group"
                  disabled={isLoading}
                >
                  <MessageCirclePlus className="size-4 group-hover:rotate-12 transition-all" />
                </button>
              )}
              <button
                type={isLoading ? 'button' : 'submit'}
                className={`p-2 rounded-full border border-input bg-background hover:bg-accent transition-colors duration-200 ${isLoading ? 'animate-pulse' : ''} ${input.length === 0 && !isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={input.length === 0 && !isLoading}
                onClick={isLoading ? stop : undefined}
              >
                {isLoading ? <Square size={20} /> : <ArrowUp size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Empty screen suggestions - shown when no messages and focused */}
        {messages.length === 0 && showEmptyScreen && (
          <div className="mt-4 space-y-4">
            <h3 className="text-sm font-medium">Try asking about:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                'What are the best beaches in Tampa?',
                'Plan a 3-day trip to Orlando with kids',
                'Best time to visit San Francisco',
                'Hidden gems in California wine country',
              ].map((suggestion, i) => (
                <button
                  key={i}
                  className="text-left p-3 border rounded-lg text-sm hover:bg-primary/5 transition-colors"
                  onClick={() => onQuerySelect(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

// Feature card component for better reusability
const FeatureCard = ({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  description?: string;
  onClick?: () => void;
}) => (
  <Card
    className="bg-primary/5 hover:bg-primary/10 transition-all duration-300 cursor-pointer hover:shadow-md"
    onClick={onClick}
  >
    <CardContent className="p-6 flex flex-col items-center text-center">
      <div className="bg-primary/10 p-3 rounded-full mb-3">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="font-medium text-base mb-1">{title}</h3>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </CardContent>
  </Card>
);

// Destination card component for consistent styling
const DestinationCard = ({ city, imageFilename }: { city: string; imageFilename: string }) => (
  <Card className="group overflow-hidden border-0 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300">
    <div className="relative h-60">
      <CloudinaryImage
        src={`/images/${imageFilename}.jpg`}
        alt={city}
        fill
        className="object-cover transition-transform duration-700 group-hover:scale-110"
        fallbackSrc="/images/placeholder-image.jpg"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent group-hover:from-black/70 transition-colors" />
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-white text-xl font-semibold mb-1">{city}</h3>
        <div className="flex items-center">
          <Button
            variant="secondary"
            size="sm"
            className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            Explore <MapPin className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  </Card>
);

export default function NextGenTravelSearchPage() {
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState('');

  const handlePlanTrip = async () => {
    try {
      console.log('Planning trip...', searchInput);
      toast({
        title: 'Trip Planning Started',
        description: 'Our AI is working on your perfect itinerary.',
      });
    } catch (error) {
      console.error('Error planning trip:', error);
      toast({
        title: 'Error',
        description: 'Failed to start trip planning. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <SessionProvider>
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section with Enhanced Design */}
        <div className="container mx-auto px-4 py-12 pt-24 md:pt-32">
          <div className="text-center space-y-6 mb-12">
            <div className="inline-block mb-2">
              <div className="flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                <span>Powered by AI & RAG Technology</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-600">
              AI Travel Assistant
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Your personal AI travel planner - Get instant recommendations, build itineraries, and
              explore destinations with ease.
            </p>
          </div>

          {/* Main Search Input with Enhanced Design */}
          <div className="max-w-3xl mx-auto">
            <Card className="bg-background/70 backdrop-blur-md border-2 shadow-lg overflow-hidden rounded-xl">
              <CardContent className="p-6 md:p-8">
                <Tabs defaultValue="text" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/50">
                    <TabsTrigger
                      value="text"
                      className="data-[state=active]:bg-background rounded-md py-3"
                    >
                      <Search className="mr-2 h-4 w-4" /> Text Search
                    </TabsTrigger>
                    <TabsTrigger
                      value="voice"
                      className="data-[state=active]:bg-background rounded-md py-3"
                    >
                      <Globe className="mr-2 h-4 w-4" /> Voice Search
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="text">
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Describe your dream trip... (e.g. 'Plan a 2-week summer vacation in Europe with my family')"
                        className="min-h-[120px] text-lg mb-4 resize-none border-2 focus:border-primary/50"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                      />
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Button
                          className="flex-1 py-6 text-base font-medium"
                          size="lg"
                          onClick={handlePlanTrip}
                        >
                          <Search className="mr-2 h-5 w-5" />
                          Plan My Trip
                        </Button>
                        <Link href="/destinations" passHref className="flex-1">
                          <Button
                            variant="outline"
                            size="lg"
                            className="w-full py-6 text-base font-medium"
                          >
                            <MapPin className="mr-2 h-5 w-5" />
                            Browse Destinations
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="voice">
                    <div className="text-center py-12">
                      <Button variant="outline" size="lg" className="rounded-full w-20 h-20">
                        <Globe className="h-8 w-8" />
                      </Button>
                      <p className="mt-4 text-muted-foreground">Click to start voice search</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Service Categories */}
          <div className="mt-16">
            <h2 className="text-2xl font-semibold mb-6 text-center">Travel Services</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <FeatureCard icon={Plane} title="Flights" description="Find the best deals" />
              <FeatureCard icon={Hotel} title="Hotels" description="Comfortable stays" />
              <FeatureCard icon={Car} title="Car Rentals" description="Explore at your pace" />
              <FeatureCard
                icon={Utensils}
                title="Experiences"
                description="Unforgettable moments"
              />
            </div>
          </div>

          {/* AI Chat Interface - Simplified UI */}
          <div className="mt-16 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Smart Travel Chat
              </h2>
              <p className="text-muted-foreground mt-1 md:mt-0">
                Ask about destinations, travel tips, or plan your next adventure
              </p>
            </div>
            <Card className="bg-background/70 backdrop-blur-md border shadow-md rounded-xl overflow-hidden">
              <CardContent className="p-6">
                <TravelChat id="next-gen-search" />
              </CardContent>
            </Card>
          </div>

          {/* Popular Destinations with Enhanced Design */}
          <div className="mt-16 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Popular Destinations</h2>
              <Button variant="ghost" size="sm" className="gap-1">
                <span>View all</span>
                <TrendingUp className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { city: 'Tampa', filename: 'tampa-fl', description: 'Florida' },
                { city: 'Orlando', filename: 'orlando-fl', description: 'Florida' },
                { city: 'San Francisco', filename: 'san-francisco', description: 'California' },
              ].map(dest => (
                <DestinationCard key={dest.city} city={dest.city} imageFilename={dest.filename} />
              ))}
            </div>
          </div>

          {/* Extra Section: Travel Planning Features */}
          <div className="mt-16 mb-8">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-0 shadow-sm overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Travel Planning Made Simple
                </CardTitle>
                <CardDescription>
                  Use our AI-powered tools to plan your next adventure
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-white/70 dark:bg-background/70 backdrop-blur-sm">
                  <CardContent className="p-4 text-center">
                    <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <Search className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-medium">Smart Search</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Find what you&apos;re looking for instantly
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white/70 dark:bg-background/70 backdrop-blur-sm">
                  <CardContent className="p-4 text-center">
                    <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-medium">AI Recommendations</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Personalized suggestions just for you
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white/70 dark:bg-background/70 backdrop-blur-sm">
                  <CardContent className="p-4 text-center">
                    <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-medium">Curated Itineraries</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Day-by-day plans for your trips
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
              <CardFooter className="justify-center pb-6">
                <Button className="bg-primary/90 hover:bg-primary">Start Planning Now</Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </SessionProvider>
  );
}
